// Animation durations in milliseconds
export const duration = {
  fastest: 150,
  fast: 200,
  normal: 300,
  slow: 400,
  slower: 500,
  slowest: 700,
};

// Easing functions
export const easing = {
  linear: 'linear',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  thermal: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
};

// Animation keyframes
export const keyframes = {
  // Thermal pulse animation
  thermalPulse: {
    '0%': {
      transform: 'scale(1)',
      opacity: '0.8',
    },
    '50%': {
      transform: 'scale(1.05)',
      opacity: '1',
    },
    '100%': {
      transform: 'scale(1)',
      opacity: '0.8',
    },
  },

  // Thermal ripple animation
  thermalRipple: {
    '0%': {
      transform: 'scale(0)',
      opacity: '1',
    },
    '100%': {
      transform: 'scale(4)',
      opacity: '0',
    },
  },

  // Fade in
  fadeIn: {
    '0%': {
      opacity: '0',
    },
    '100%': {
      opacity: '1',
    },
  },

  // Fade out
  fadeOut: {
    '0%': {
      opacity: '1',
    },
    '100%': {
      opacity: '0',
    },
  },

  // Slide up
  slideUp: {
    '0%': {
      transform: 'translateY(20px)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: '1',
    },
  },

  // Slide down
  slideDown: {
    '0%': {
      transform: 'translateY(-20px)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: '1',
    },
  },

  // Slide left
  slideLeft: {
    '0%': {
      transform: 'translateX(20px)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: '1',
    },
  },

  // Slide right
  slideRight: {
    '0%': {
      transform: 'translateX(-20px)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: '1',
    },
  },

  // Scale in
  scaleIn: {
    '0%': {
      transform: 'scale(0.9)',
      opacity: '0',
    },
    '100%': {
      transform: 'scale(1)',
      opacity: '1',
    },
  },

  // Scale out
  scaleOut: {
    '0%': {
      transform: 'scale(1)',
      opacity: '1',
    },
    '100%': {
      transform: 'scale(0.9)',
      opacity: '0',
    },
  },

  // Rotate
  rotate: {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },

  // Bounce
  bounce: {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
  },

  // Glow
  glow: {
    '0%, 100%': {
      boxShadow: '0 0 5px rgba(0, 255, 255, 0.5)',
    },
    '50%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.8), 0 0 30px rgba(0, 255, 255, 0.6)',
    },
  },

  // Scan line
  scanLine: {
    '0%': {
      transform: 'translateY(-100%)',
    },
    '100%': {
      transform: 'translateY(100vh)',
    },
  },

  // Particle float
  particleFloat: {
    '0%, 100%': {
      transform: 'translateY(0) translateX(0)',
    },
    '25%': {
      transform: 'translateY(-10px) translateX(5px)',
    },
    '50%': {
      transform: 'translateY(0) translateX(10px)',
    },
    '75%': {
      transform: 'translateY(10px) translateX(5px)',
    },
  },
};

// Animation presets
export const animations = {
  // Thermal pulse
  thermalPulse: `${duration.normal}ms ${easing.thermal} infinite`,
  thermalPulseSlow: `${duration.slowest}ms ${easing.thermal} infinite`,

  // Thermal ripple
  thermalRipple: `${duration.slower}ms ${easing.easeOut} forwards`,

  // Fade
  fadeIn: `${duration.normal}ms ${easing.easeOut} forwards`,
  fadeOut: `${duration.normal}ms ${easing.easeIn} forwards`,

  // Slide
  slideUp: `${duration.normal}ms ${easing.easeOut} forwards`,
  slideDown: `${duration.normal}ms ${easing.easeOut} forwards`,
  slideLeft: `${duration.normal}ms ${easing.easeOut} forwards`,
  slideRight: `${duration.normal}ms ${easing.easeOut} forwards`,

  // Scale
  scaleIn: `${duration.normal}ms ${easing.spring} forwards`,
  scaleOut: `${duration.normal}ms ${easing.easeIn} forwards`,

  // Rotate
  rotate: `${duration.slower}ms ${easing.linear} infinite`,

  // Bounce
  bounce: `${duration.slow}ms ${easing.easeInOut} infinite`,

  // Glow
  glow: `${duration.slowest}ms ${easing.easeInOut} infinite`,

  // Scan line
  scanLine: `${duration.slower * 3}ms ${easing.linear} infinite`,

  // Particle float
  particleFloat: `${duration.slowest}ms ${easing.easeInOut} infinite`,
};

// Transition presets
export const transitions = {
  // Fast transitions
  fast: `all ${duration.fast}ms ${easing.easeOut}`,
  faster: `all ${duration.fastest}ms ${easing.easeOut}`,

  // Normal transitions
  default: `all ${duration.normal}ms ${easing.easeOut}`,
  easeIn: `all ${duration.normal}ms ${easing.easeIn}`,
  easeInOut: `all ${duration.normal}ms ${easing.easeInOut}`,

  // Slow transitions
  slow: `all ${duration.slow}ms ${easing.easeOut}`,
  slower: `all ${duration.slower}ms ${easing.easeOut}`,

  // Spring transitions
  spring: `all ${duration.normal}ms ${easing.spring}`,
  thermal: `all ${duration.normal}ms ${easing.thermal}`,

  // Theme transition
  theme: `background-color ${duration.normal}ms ${easing.easeInOut}, color ${duration.normal}ms ${easing.easeInOut}, border-color ${duration.normal}ms ${easing.easeInOut}`,

  // Hover transitions
  hover: `transform ${duration.fast}ms ${easing.easeOut}, box-shadow ${duration.fast}ms ${easing.easeOut}`,
  hoverSlow: `transform ${duration.normal}ms ${easing.easeOut}, box-shadow ${duration.normal}ms ${easing.easeOut}`,
};